/* @flow */

import {
  normalizeMembership,
  getMembershipId,
  getMembershipGuidanceURL,
} from '@braindate/domain/lib/membership/util';
import { isObject } from '@braindate/util/lib/type';

import * as types from './membershipActionTypes';
import { patchEntities } from 'src/shared/domain/base/action/entityActions';
import {
  membershipEndpoint,
  membershipCalendarSyncStatusEndpoint,
  membershipGuidanceEndpoint,
} from 'src/shared/app/base/settings/endpointSettings';
import { parseResponse } from 'src/shared/app/base/util/ajaxUtils';

import type { Membership } from '@braindate/domain/lib/membership/type';
import type { GetState } from 'src/shared/core/type/reduxTypes';

// Fetch

export function fetchMembership(eventId: number | string, data?: Object) {
  return (dispatch: any, _: GetState, { get }: Object) => {
    dispatch(fetchMembershipRequest());

    return get(membershipEndpoint(eventId), data)
      .then(parseResponse)
      .then((json) => {
        const normalizedData = normalizeMembership(json);
        const { entities, result } = normalizedData;

        dispatch(patchEntities(entities));
        // $FlowIssue
        dispatch(fetchMembershipSuccess(result));

        return result;
      })
      .catch((exception) => {
        dispatch(fetchMembershipFailure(exception));

        throw exception;
      });
  };
}

export function fetchMembershipRequest() {
  return {
    type: types.FETCH_MEMBERSHIP_REQUEST,
  };
}

export function fetchMembershipSuccess(membershipId: number) {
  return {
    type: types.FETCH_MEMBERSHIP_SUCCESS,
    membershipId,
  };
}

export function fetchMembershipFailure(exception: Error) {
  return {
    type: types.FETCH_MEMBERSHIP_FAILURE,
    exception,
  };
}

export function fetchMembershipOptimally(eventId: number | string) {
  return fetchMembership(eventId);
}

// Patch

export function patchMembership(eventId: number, data: Object) {
  return (dispatch: any, _: GetState, { patch }: Object) => {
    dispatch(patchMembershipRequest());

    return patch(membershipEndpoint(eventId), data)
      .then(parseResponse)
      .then((json) => {
        const normalizedData = normalizeMembership(json);
        const { entities, result } = normalizedData;

        dispatch(patchEntities(entities));
        // $FlowIssue
        dispatch(patchMembershipSuccess(result));
      })
      .catch((exception) => {
        dispatch(patchMembershipFailure(exception));

        throw exception;
      });
  };
}

export function patchMembershipRequest() {
  return {
    type: types.PATCH_MEMBERSHIP_REQUEST,
  };
}

export function patchMembershipSuccess(result: number) {
  return {
    type: types.PATCH_MEMBERSHIP_SUCCESS,
    result,
  };
}

export function patchMembershipFailure(exception: Error) {
  return {
    type: types.PATCH_MEMBERSHIP_FAILURE,
    exception,
  };
}

// Reset

export function resetMembership() {
  return {
    type: types.RESET_MEMBERSHIP,
  };
}

// Calendar Sync Status

export function fetchOauthStatus(id: number) {
  return (dispatch: any, _: GetState, { smartGet }: Object) => {
    dispatch(fetchOauthStatusRequest());

    return smartGet(membershipCalendarSyncStatusEndpoint(id))
      .then((json) => dispatch(fetchOauthStatusSuccess(json)))
      .catch((exception) => {
        dispatch(fetchOauthStatusFailure(exception));

        throw exception;
      });
  };
}

export function fetchOauthStatusRequest() {
  return {
    type: types.FETCH_CALENDAR_SYNC_STATUS_REQUEST,
  };
}

export function fetchOauthStatusSuccess(result: Object) {
  return {
    type: types.FETCH_CALENDAR_SYNC_STATUS_SUCCESS,
    result,
  };
}

export function fetchOauthStatusFailure(exception: Error) {
  return {
    type: types.FETCH_CALENDAR_SYNC_STATUS_FAILURE,
    exception,
  };
}

export function updateCalendarSyncStatus(data: Object) {
  return fetchOauthStatusSuccess(data);
}

export function fetchGuidanceMarketPrompt(membership: Membership) {
  return (dispatch: any, _: GetState, { smartGet }: Object) => {
    dispatch(fetchGuidanceMarketPromptRequest());
    return smartGet(
      getMembershipGuidanceURL(membership) ||
        membershipGuidanceEndpoint(getMembershipId(membership)),
    )
      .then((json) =>
        dispatch(fetchGuidanceMarketPromptSuccess(json, membership)),
      )
      .catch((exception) =>
        dispatch(fetchGuidanceMarketPromptFailure(exception)),
      );
  };
}

export function fetchGuidanceMarketPromptRequest() {
  return {
    type: types.FETCH_GUIDANCE_MARKET_PROMPT_REQUEST,
  };
}

export function fetchGuidanceMarketPromptSuccess(
  result: Object,
  membership: Membership,
) {
  return {
    type: types.FETCH_GUIDANCE_MARKET_PROMPT_SUCCESS,
    guidance: result,
    membership,
  };
}

export function fetchGuidanceMarketPromptFailure(exception: Error) {
  return {
    type: types.FETCH_GUIDANCE_MARKET_PROMPT_FAILURE,
    exception,
  };
}

export function deleteGuidanceMarketPrompt(
  membership: Membership,
  label: string,
  data?: Object,
) {
  let params = { label };

  if (isObject(data)) {
    params = {
      ...params,
      ...data,
    };
  }

  return (dispatch: any, _: GetState, { deleteFn }: Object) => {
    dispatch(deleteGuidanceMarketPromptRequest());
    return deleteFn(getMembershipGuidanceURL(membership), params)
      .then(() => dispatch(deleteGuidanceMarketPromptSuccess(membership)))
      .catch((exception) =>
        dispatch(deleteGuidanceMarketPromptFailure(exception)),
      );
  };
}

export function deleteGuidanceMarketPromptRequest() {
  return {
    type: types.DELETE_GUIDANCE_MARKET_PROMPT_REQUEST,
  };
}

export function deleteGuidanceMarketPromptSuccess(membership: Membership) {
  return {
    type: types.DELETE_GUIDANCE_MARKET_PROMPT_SUCCESS,
    membership,
  };
}

export function deleteGuidanceMarketPromptFailure(exception: Error) {
  return {
    type: types.DELETE_GUIDANCE_MARKET_PROMPT_FAILURE,
    exception,
  };
}
